/* eslint-disable @typescript-eslint/no-shadow */
import axios, { AxiosError } from 'axios';
import { handleError, oneData, allData } from './normalizer';
import { Identifiable, CreateCRUDBase } from './types';

export const BASE_URL = `${process.env.REACT_APP_API_BASE}`;
export const KIOSK_URL = `${process.env.REACT_APP_KIOSK_BASE}`;

export const defaultHeaders: HeadersInit = new Headers({
  Accept: 'application/json',
  'Content-Type': 'application/json'
});

interface AuthHeader {
  Authorization?: string;
}

export class API<T extends Identifiable, U extends Identifiable, V> implements CreateCRUDBase<T, U, V> {
  public entity: string;

  public constructor(entity: string) {
    this.entity = entity;
  }

  public get url(): string {
    return `${BASE_URL}/admin/${this.entity}`;
  }

  static getAuthHeader = (): AuthHeader => {
    const token = localStorage.getItem('token');
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  };

  public getAll = async <T>(
    query?: { [K in keyof T]: string },
    headers: HeadersInit = {},
    timeout: number = 0
  ): Promise<T[]> => {
    try {
      return allData(
        await axios.get(this.url, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader() },
          params: query,
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };

  public getOne = async <T>(id: string, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.get(`${this.url}/${id}`, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };

  public create = async <T, V>(data: V, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.post(`${this.url}`, data, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };

  public upload = async <T>(
    payload: {
      file: Blob;
      name: string;
      fileName?: string;
      isEventImage?: boolean;
      type?: string;
      configurationId?: string;
      clientIntegrationId?: string;
      mediaId?: string;
    },
    headers: HeadersInit = {},
    timeout: number = 0
  ): Promise<T> => {
    try {
      const formData = new FormData();
      formData.set('file', payload.file, payload.name);
      if (payload.type) formData.set('type', payload.type);
      if (payload.isEventImage) formData.set('isEventImage', `${payload.isEventImage}`);
      if (payload.configurationId) formData.set('configurationId', payload.configurationId);
      if (payload.clientIntegrationId) formData.set('clientIntegrationId', payload.clientIntegrationId);
      if (payload.mediaId) formData.set('mediaId', payload.mediaId);
      if (payload.fileName) formData.set('fileName', payload.fileName);

      return oneData(
        await axios.post(`${this.url}`, formData, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader(), 'content-type': 'multipart/form-data' },
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };

  public update = async <T, U extends Identifiable>(
    data: U,
    headers: HeadersInit = {},
    timeout: number = 0
  ): Promise<T> => {
    const { id, ...rest } = data;
    try {
      return oneData(
        await axios.put(`${this.url}/${id}`, rest, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };

  public destroy = async <T>(id: string, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.delete(`${this.url}/${id}`, {
          headers: { ...defaultHeaders, ...headers, ...API.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      return handleError(err as AxiosError);
    }
  };
}

export class AdminAPI<T extends Identifiable, U extends Identifiable, V> extends API<T, U, V> {
  public get url(): string {
    return this.entity;
  }
  static getAuthHeader = (): AuthHeader => {
    return {};
  };
}
